import React from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';

import InputTextField from './InputTextField';
import './TransactionTable.scss';
import featureFlags from '../../featureFlags';
import {paymentFields, PaymentHistory} from '../../Utils/PaymentHistory';

import { queryUpdatePayment } from '../../Queries/Payment';

class TransactionTable extends React.Component {
  state = {
    editMode: {
      value: false,
      index: null
    },
    payments: [],
  }
  
  paymentHistory = null;
  invoiceId = this.props.invoiceId;
  facilityId;
  subscriptionId;

  editStateStoredNote = null;
  
  async componentDidMount() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    this.subscriptionId = urlSearchParams.get('subscriptionId');
    this.facilityId = urlSearchParams.get('facility');

    this.paymentHistory = new PaymentHistory({
      invoiceId: this.invoiceId,
    });
    
    await this.paymentHistory.initialize();
    const results = await this.paymentHistory.fetchAll();
    const payments = this.createRows(results);
    this.setState({ payments });
  }

  handleTransactionNotesEditClick(index) {
    this.props.onEditButtonClick().then(() => {
      this.editStateStoredNote = this.state.payments[index].notes;
    
      const editMode = {
        value: true,
        index: index
      }
      this.setState({ editMode })
    })
  }

  handleTransactionNotesSaveClick = () => {
    const { index } = this.state.editMode;

    const { id: payment_id, notes } = this.state.payments[index];
    const args = {
      payment_id,
      notes,
    };
    this.saveNotes(args).then(() => {
      this.props.onCancelButtonClick();
      this.editStateStoredNote = null;

      const editMode = {
        value: false,
        index: null
      };
      this.setState({ editMode });
    });
  }

  saveNotes(paymentAttrs) {
    return new Promise((resolve, reject) => {
      queryUpdatePayment(paymentAttrs, paymentFields).then((response) => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }
        resolve(response.data.updatePayment);
      }).catch((error) => {
        reject([error]);
      });

    });
  }

  handleTransactionNotesCancelClick = () => {
    const payments = _.cloneDeep(this.state.payments);
    payments[this.state.editMode.index].notes = this.editStateStoredNote;
    this.editStateStoredNote = null;

    const editMode = {
      value: false,
      index: null
    }
    this.setState({ editMode, payments });

    this.props.onCancelButtonClick();
  }

  _storeNotesChange(index) {
    return note => {
      const payments = _.cloneDeep(this.state.payments);

      payments[index].notes = note;
      this.setState({ payments });
    }
  }

  getRowValues(data) {
    const row = {
      model: data.model,
      id: data.paymentId,
      date: null,
      applicationDate: null,
      type: null,
      invoiceNumber: null,
      creditId: null,
      paymentId: null,
      amount: null,
      balance: null,
      creditAmount: null,
      notes: null,
    };
    let page = null;
    let url = null;
    Object.keys(row).forEach((column) => {
      const value = data[column];
      switch (column) {
        case 'date':
          if (data.timestamp) {
            const {facilityTimezone} = this.paymentHistory.fieldConfiguration;
            row[column] = moment(moment(data.timestamp, 'x').tz(facilityTimezone));
          }
          break;
        case 'applicationDate':
          if (value) {
            const date = moment(value);
            row[column] = date.format('M/D/YYYY');
          }
          break;
        case 'type':
          let type;
          if (data.model === 'CREDIT') {
            type = this.paymentHistory.fieldConfiguration.transaction['type'].options.find(
              option => option.value === value
            );
            row[column] = PaymentHistory.getType(data.appType, `Credit - ${type?.label}`);
          } else {
            type = this.paymentHistory.fieldConfiguration.transaction['type'].options.find(
              option => option.value === value
            );
            row[column] = PaymentHistory.getType(data.appType, type?.label);
          }
          break;
        case 'invoiceNumber':
          if (data.invoiceId && data.invoiceNumber) {
            page = `invoice&invoice=${data.invoiceId}`;
            url = `?page=parkchirp_${page}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}`;
            row.invoiceNumber = (
              <a href={url}>{data.invoiceNumber}</a>
            );

          }
          break;
        case 'creditId':
          if (data.creditId && data.creditNumber) {
            page = `credit&credit=${data.creditId}`;
            url = `?page=parkchirp_${page}${(this.facilityId !== null) ? `&facility=${this.facilityId}` : ''}`;
            row.creditNumber = (
              <a href={url}>{data.creditNumber}</a>
            );
          }
          break;
        case 'paymentId':
          if (data.paymentMeta) {
            const meta = (data.paymentMeta);
            if (meta?.payment?.reference_id) {
              row[column] = meta?.payment?.reference_id;
            } else if (meta?.check_number) {
              row[column] = meta?.check_number;
            }
          }
          break;
        case 'notes':
        case 'amount':
        case 'balance':
        case 'creditAmount':
          row[column] = data[column];
          break;
        default: break;
      }
    });
    return row;
  }

  createRows(results) {
    const rows = PaymentHistory.collectDataRows(results).map((row) => this.getRowValues(row));
    const sorted = this.sortRows(rows);

    // Format transaction dates and amounts
    // Running balance
    let balance = 0;
    return sorted.map((row) => {
      if (row.date && moment.isMoment(row.date)) {
        row.date = row.date.format('M/D/YYYY');
      }

      // Adjustments will have already decreased the Invoice balance by decreasing the (original) amount and
      // must therefore be excluded from the running balance.
      if (row.amount !== null) {
        balance += row.amount;
        row.balance = PaymentHistory.formatCurrency(balance);
      }

      row.creditAmount = PaymentHistory.formatCurrency(row.creditAmount);
      row.amount = PaymentHistory.formatCurrency(row.amount);

      return row;
    });
  }
  
  sortRows(rows, sort = 'date', order = -1) {
    return rows.sort((a, b) => {
      switch (sort) {
        case 'date':
        case 'applicationDate':
          const ad = typeof a[sort] === 'string' ? moment(a[sort]) : a[sort];
          const bd = typeof b[sort] === 'string' ? moment(b[sort]) : b[sort];
          return (ad > bd) ? -order : (ad === bd) ? 0 : (order);
        case 'type':
        case 'notes':
          return (a[sort] ?? '').localeCompare(b[sort] ?? '') * order;
        case 'amount':
        case 'balance':
        case 'creditAmount':
          const aVal = a[sort] ?? 0;
          const bVal = b[sort] ?? 0;
          return (aVal - bVal) * order;
        default: return 1;
      }
    });
  }

  render() {
    const { payments } = this.state;
    const { disabled } = this.props;

    // date, application_date, type, invoice_number, credit_id, payment_id, amount, balance, notes
    return (
      <table className="widefat parkchirp--transaction-table">
        <thead>
        <tr>
          <th>{featureFlags.APPLICATION_DATE ? "Trans. Date" : "Date"}</th>
          {featureFlags.APPLICATION_DATE && <th>App. Date</th>}
          <th>Type</th>
          <th>Credit ID</th>
          <th>Payment ID</th>
          <th>Amount</th>
          <th>Current Balance</th>
          <th>Credit Amount</th>
          <th colSpan='2'>Transaction Notes</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        {payments.map((payment, i) => {
          // Can we view, store, and save notes for this payment?
          const notable = ['PAYMENT', 'REVERSAL', 'CREDIT', 'LINE_ITEM_ENTRY'].includes(payment.model);
          const rowEditMode = this.state.editMode.value && this.state.editMode.index === i;
          return (
            <tr key={i}>
              <td>
                <span>{payment.date}</span>
              </td>
              {featureFlags.APPLICATION_DATE &&
                <td>
                  <span>{payment.applicationDate}</span>
                </td>
              }
              <td>
                <span>{payment.type}</span>
              </td>
              <td>
                <span>{payment.creditNumber}</span>
              </td>
              <td>
                <span>{payment.paymentId}</span>
              </td>
              <td>
                <span>{payment.amount}</span>
              </td>
              <td>
                <span>{payment.balance}</span>
              </td>
              <td>
                <span>{payment.creditAmount}</span>
              </td>
              <td colSpan='2'>
                { notable && (
                    <InputTextField inputId="parkchirp--transaction-table--notes"
                                    value={payment.notes}
                                    onChange={this._storeNotesChange(i)}
                                    multiLine={true}
                                    editMode={rowEditMode}
                    />
                ) }
              </td>
              { notable && rowEditMode ?
                <td className='parkchirp--transaction-table--buttons'>
                  <div>
                    <button type="button"
                            class="parkchirp--transaction-table--save-button parkchirp--button-as-link"
                            onClick={this.handleTransactionNotesSaveClick}>
                      Save
                    </button>
                  </div>
                  <div>
                    <button type="button"
                            class="parkchirp--transaction-table--cancel-button parkchirp--button-as-link"
                            onClick={this.handleTransactionNotesCancelClick}>
                      Cancel
                    </button>
                  </div>
                </td>
                :
                !notable || this.state.editMode.value || disabled ?
                  <td></td>
                  :
                  <td>
                    <button type="button"
                            class="parkchirp--transaction-table--edit-button parkchirp--button-as-link"
                            onClick={() => this.handleTransactionNotesEditClick(i)}>
                      Edit
                    </button>
                  </td>
              }
            </tr>
          )}
        )}
        </tbody>
      </table>
    );
  }
}

export default TransactionTable;